import { addMonths, isBefore, parse, parseISO } from 'date-fns';
import { infer as Infer, z } from 'zod';

import { locationSchema } from '#location/locationCreate.schema';
import { linkURLRegex } from '#utils/linkURL.regex';

export const groupScheduleMeetingSchema = z.codec(
  z
    .object({
      startT: z.iso.time({ message: 'Czas rozpoczęcia jest wymagany' }),
      endT: z.iso.time({ message: 'Czas zakończenia jest wymagany' }),
      type: z.enum(['remote', 'stationary']),
      name: z.string({ message: 'Nazwa jest wymagana' }).min(1, 'Nazwa jest wymagana'),
      repeat: z.enum(['weekly', 'biweekly', 'monthly']),
      day: z.number().min(1).max(7),
      location: locationSchema.nullable().optional(),
      meetingURL: z.string().regex(linkURLRegex, 'Niepoprawny format linku').nullable(),
      skipHolidays: z.boolean().optional(),
    })
    .refine(
      (value) => {
        const { startT, endT } = value;
        return isBefore(parse(startT, 'HH:mm', new Date()), parse(endT, 'HH:mm', new Date()));
      },
      {
        message: 'Czas zakończenia musi być po czasie rozpoczęcia',
        path: ['endT'],
      }
    ),
  z
    .object({
      startT: z.iso.time({ message: 'Czas rozpoczęcia jest wymagany' }),
      endT: z.iso.time({ message: 'Czas zakończenia jest wymagany' }),
      type: z.enum(['remote', 'stationary']),
      name: z.string({ message: 'Nazwa jest wymagana' }).min(1, 'Nazwa jest wymagana'),
      repeat: z.enum(['weekly', 'biweekly', 'monthly']),
      day: z.number().min(1).max(7),
      locationId: z.number().nullable().optional(),
      meetingURL: z.string().regex(linkURLRegex, 'Niepoprawny format linku').nullable(),
      skipHolidays: z.boolean().optional(),
    })
    .refine(
      (value) => {
        const { startT, endT } = value;
        return isBefore(parse(startT, 'HH:mm', new Date()), parse(endT, 'HH:mm', new Date()));
      },
      {
        message: 'Czas zakończenia musi być po czasie rozpoczęcia',
        path: ['endT'],
      }
    ),
  {
    decode: (meeting) => ({
      ...meeting,
      locationId: meeting.location?.id ?? null,
      meetingURL: meeting.meetingURL ?? null,
      skipHolidays: meeting.skipHolidays ?? false,
    }),
    encode: (meeting) => ({
      ...meeting,
      location: undefined,
      skipHolidays: meeting.skipHolidays ?? false,
    }),
  }
);

export type GroupScheduleMeetingSchema = Infer<typeof groupScheduleMeetingSchema.in>;
export type GroupScheduleMeetingSchemaOutput = Infer<typeof groupScheduleMeetingSchema.out>;

export const groupScheduleSchema = z
  .object({
    startDate: z.iso.date({ message: 'Data rozpoczęcia jest wymagana' }),
    endDate: z.iso.date({ message: 'Data zakończenia jest wymagana' }),
    meetings: z.array(groupScheduleMeetingSchema.in),
  })
  .refine(
    (value) => {
      const { startDate, endDate } = value;
      return isBefore(new Date(startDate), new Date(endDate));
    },
    {
      message: 'Data zakończenia musi być po dacie rozpoczęcia',
      path: ['startDate'],
    }
  )
  .refine(
    (value) => {
      const start = parseISO(value.startDate);
      const end = parseISO(value.endDate);
      return !isBefore(end, addMonths(start, 1));
    },
    {
      message: 'Zakres dat harmonogramu musi wynosić co najmniej miesiąc',
      path: ['endDate'],
    }
  );

export type GroupScheduleSchema = Infer<typeof groupScheduleSchema>;

export const groupScheduleFormSchema = groupScheduleSchema.safeExtend({
  skipHolidays: z.boolean(),
});

export type GroupScheduleFormSchema = Infer<typeof groupScheduleFormSchema>;
